import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SocialAuthService, SocialUser } from '@abacritt/angularx-social-login';
import { DataService } from '../store/data.service';
import { AuthUser } from '../../auth/AuthUser';
import { AuthResponse } from '../../interfaces/response/AuthResponse';

/**
 * This service handles login and logout with SSO.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private socialAuthService = inject(SocialAuthService);
  private dataService = inject(DataService);

  url = environment.apiUrl + '/auth';

  /**
   * An authenticated user object which can be subscribed and used in any place of application.
   */
  user = new BehaviorSubject<AuthUser>(null);

  /**
   * Sends a request to backend in order to authenticate, and saves user data. This method requires {@link SocialUser} provided by
   * logging in via SSO.
   * @param socialUser SSO user object
   */
  login(socialUser: SocialUser): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(this.url, { idToken: socialUser.idToken })
      .pipe(
        catchError(error => this.handleError(error)),
        tap(response => this.handleAuthentication(socialUser.name, response))
      );
  }

  /**
   * Logs in to Google via SSO.
   */
  loginSso(): Observable<SocialUser> {
    return this.socialAuthService.authState;
  }

  /**
   * Logs in automatically if user data is stored in local storage. This method is useful when a user leaves a page but later returns.
   */
  autoLogin() {
    const userData: AuthUser = this.dataService.userData;
    if (userData && userData.jwtToken) {
      this.user.next(userData);
    }
  }

  /**
   * Tries to log out with SSO, clears user data and goes to login page. User data should be cleared either if logging
   * out with SSO is successful or not. Logging out can be unsuccessful if user token was expired.
   */
  logout() {
    return this.socialAuthService.signOut().then(() => {
      this.clearUserDataAndGoToLoginPage();
    }, () => {
      this.clearUserDataAndGoToLoginPage();
    });
  }

  private clearUserDataAndGoToLoginPage() {
    this.user.next(null);
    localStorage.clear();
    this.router.navigate(['/login']);
  }

  private handleAuthentication(username: string, authResponse: AuthResponse) {
    const user = new AuthUser(username, authResponse.jwtToken, authResponse.authorities);
    this.user.next(user);
    this.dataService.userData = user;
  }

  private handleError(errorResponse: HttpErrorResponse) {
    const unknownErrorMessage = 'An unknown error occurred!';
    if (!errorResponse.error || !errorResponse.error.error) {
      return throwError(unknownErrorMessage);
    }
    return throwError(errorResponse.error.error);
  }

}
